// src/components/admin/employees/EmployeeList.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Edit, Trash2, Eye, Mail, Phone, UserPlus, X, Save, XCircle, Shield, ChevronDown, Plus, Filter } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { useEmployeeUpdates } from '../../../hooks/useEmployeeUpdates';
import { getExitStatusBadge } from '../../../utils/exitStatusHelper';

const EmployeeList = ({ employees, departments, showCreateAccessModal, setShowCreateAccessModal }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { updateEmployee, deleteEmployee, fetchEmployees } = useEmployees();

    // Listen for employee updates
    useEmployeeUpdates((action, data) => {
        console.log('EmployeeList received update:', action, data);
        // Force refresh of employees when updates occur
        fetchEmployees(true);
    });
    const [showManagerModal, setShowManagerModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedManagerId, setSelectedManagerId] = useState('');
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [openAccessDropdown, setOpenAccessDropdown] = useState(null);
    const [scrollHintActive, setScrollHintActive] = useState(false);
    const [scrollHintHidden, setScrollHintHidden] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollHintBurstTimeoutRef = useRef(null);
    const scrollHintIdleTimeoutRef = useRef(null);
    const scrollHintHideDuringScrollRef = useRef(null);
    const [customAccessTypes, setCustomAccessTypes] = useState([]);
    const [newAccessType, setNewAccessType] = useState({ name: '', description: '' });

    // Filter states
    const [filters, setFilters] = useState({
        legalEntity: 'all',
        reportingManager: 'all',
        accessLevel: 'all'
    });

    const getDepartmentName = (employee) => {
        // First try to use the department name directly from employee if available
        if (employee.department_name) {
            return employee.department_name;
        }
        if (employee.department) {
            return employee.department;
        }
        // Otherwise try to find by ID
        if (employee.department_id && departments.length > 0) {
            const dept = departments.find(d => d.id === employee.department_id);
            return dept ? dept.name : 'N/A';
        }
        return 'N/A';
    };

    // Get unique values for filters
    const uniqueLegalEntities = useMemo(() => {
        const entities = new Set();
        employees.forEach(emp => {
            const entity = emp.legal_entity || emp.legalEntity;
            if (entity && entity !== 'Not specified' && entity !== 'N/A') {
                entities.add(entity);
            }
        });
        return Array.from(entities).sort();
    }, [employees]);

    const uniqueManagers = useMemo(() => {
        const managers = new Map();
        employees.forEach(emp => {
            const managerId = emp.reporting_manager_id || emp.managerId;
            if (managerId) {
                const manager = employees.find(e => e.id === managerId);
                if (manager) {
                    managers.set(managerId, {
                        id: managerId,
                        name: manager.name || `${manager.first_name} ${manager.last_name}`
                    });
                }
            }
        });
        return Array.from(managers.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [employees]);

    const uniqueAccessLevels = useMemo(() => {
        const levels = new Set();
        employees.forEach(emp => {
            const level = emp.accessLevel || 'employee';
            levels.add(level);
        });
        return Array.from(levels).sort();
    }, [employees]);

    // Filtered employees based on selected filters
    const filteredEmployees = useMemo(() => {
        return employees.filter(emp => {
            // Legal Entity filter
            if (filters.legalEntity !== 'all') {
                const empLegalEntity = emp.legal_entity || emp.legalEntity;
                if (empLegalEntity !== filters.legalEntity) {
                    return false;
                }
            }

            // Reporting Manager filter
            if (filters.reportingManager !== 'all') {
                const empManagerId = emp.reporting_manager_id || emp.managerId;
                if (empManagerId !== filters.reportingManager) {
                    return false;
                }
            }

            // Access Level filter
            if (filters.accessLevel !== 'all') {
                const empAccessLevel = emp.accessLevel || 'employee';
                if (empAccessLevel !== filters.accessLevel) {
                    return false;
                }
            }

            return true;
        });
    }, [employees, filters]);

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            legalEntity: 'all',
            reportingManager: 'all',
            accessLevel: 'all'
        });
    };

    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (filters.legalEntity !== 'all') count++;
        if (filters.reportingManager !== 'all') count++;
        if (filters.accessLevel !== 'all') count++;
        return count;
    }, [filters]);

    // Close dropdown when location changes to prevent overlay from blocking sidebar
    useEffect(() => {
        setOpenAccessDropdown(null);
    }, [location.pathname]);

    // Cleanup effect to close any open dropdowns and modals when component unmounts
    useEffect(() => {
        return () => {
            setOpenAccessDropdown(null);
            setShowManagerModal(false);
            setShowCreateAccessModal(false);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (scrollHintBurstTimeoutRef.current) clearTimeout(scrollHintBurstTimeoutRef.current);
            if (scrollHintIdleTimeoutRef.current) clearTimeout(scrollHintIdleTimeoutRef.current);
            if (scrollHintHideDuringScrollRef.current) clearTimeout(scrollHintHideDuringScrollRef.current);
        };
    }, []);

    const handleAddManager = (employee) => {
        setSelectedEmployee(employee);
        setSelectedManagerId('');
        setShowManagerModal(true);
    };

    const handleManagerSubmit = () => {
        if (selectedEmployee && selectedManagerId) {
            updateEmployee(selectedEmployee.id, { managerId: selectedManagerId });
            setShowManagerModal(false);
            setSelectedEmployee(null);
            setSelectedManagerId('');
        }
    };

    // Get all employees except the selected one as potential managers
    const potentialManagers = selectedEmployee
        ? employees.filter(emp => emp.id !== selectedEmployee.id)
        : employees;

    // Edit functionality handlers
    const handleEditEmployee = (employee) => {
        setEditingEmployee(employee.id);
        setEditFormData({
            first_name: employee.first_name,
            last_name: employee.last_name,
            email: employee.email,
            phone: employee.phone || '',
            designation: employee.designation,
            department_id: employee.department_id,
            managerId: employee.managerId || ''
        });
    };

    const handleCancelEdit = () => {
        setEditingEmployee(null);
        setEditFormData({});
    };

    const handleSaveEdit = () => {
        if (editingEmployee) {
            // Basic validation
            if (!editFormData.first_name || !editFormData.last_name || !editFormData.email || !editFormData.designation || !editFormData.department_id) {
                alert('Please fill in all required fields (First Name, Last Name, Email, Designation, Department)');
                return;
            }

            // Email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(editFormData.email)) {
                alert('Please enter a valid email address');
                return;
            }

            updateEmployee(editingEmployee, editFormData);
            setEditingEmployee(null);
            setEditFormData({});
        }
    };

    const handleEditFieldChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDeleteEmployee = (employee) => {
        if (window.confirm(`Are you sure you want to delete ${employee.first_name} ${employee.last_name}? This action cannot be undone.`)) {
            deleteEmployee(employee.id);
        }
    };

    // Access level management
    const defaultAccessTypes = [
        { id: 'admin', name: 'Admin Access', description: 'Full access to admin portal' },
        { id: 'manager', name: 'Manager Access', description: 'Access to manager portal' },
        { id: 'employee', name: 'Employee Access', description: 'Access to employee portal' }
    ];

    const allAccessTypes = [...defaultAccessTypes, ...customAccessTypes];

    const handleAccessLevelChange = (employeeId, accessLevel) => {
        updateEmployee(employeeId, { accessLevel });
        setOpenAccessDropdown(null);
    };

    const getAccessLevelColor = (accessLevel) => {
        switch(accessLevel) {
            case 'admin': return 'text-purple-600 bg-purple-50';
            case 'manager': return 'text-blue-600 bg-blue-50';
            case 'employee': return 'text-green-600 bg-green-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const handleCreateCustomAccess = () => {
        if (!newAccessType.name.trim()) {
            alert('Please enter an access type name');
            return;
        }

        const customAccess = {
            id: newAccessType.name.toLowerCase().replace(/\s+/g, '_'),
            name: newAccessType.name,
            description: newAccessType.description || `Custom access: ${newAccessType.name}`
        };

        setCustomAccessTypes([...customAccessTypes, customAccess]);
        setNewAccessType({ name: '', description: '' });
        setShowCreateAccessModal(false);
    };

    // Show empty state if no employees
    if (employees.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-8 text-center">
                <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <UserPlus className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No employees found</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">Get started by adding your first employee to the system.</p>
                {/*<button*/}
                {/*    onClick={() => navigate('/admin/employees/add')}*/}
                {/*    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"*/}
                {/*>*/}
                {/*    <UserPlus className="w-4 h-4 mr-2" />*/}
                {/*    Add Employee*/}
                {/*</button>*/}
            </div>
        );
    }

    return (
        <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-hidden">
            {/* Filters Section */}
            <div className="tour-employee-list-filters px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
                    </div>

                    {/* Legal Entity Filter */}
                    <select
                        value={filters.legalEntity}
                        onChange={(e) => handleFilterChange('legalEntity', e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Legal Entities</option>
                        {uniqueLegalEntities.map(entity => (
                            <option key={entity} value={entity}>{entity}</option>
                        ))}
                    </select>

                    {/* Reporting Manager Filter */}
                    <select
                        value={filters.reportingManager}
                        onChange={(e) => handleFilterChange('reportingManager', e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Managers</option>
                        {uniqueManagers.map(manager => (
                            <option key={manager.id} value={manager.id}>{manager.name}</option>
                        ))}
                    </select>

                    {/* Access Level Filter */}
                    <select
                        value={filters.accessLevel}
                        onChange={(e) => handleFilterChange('accessLevel', e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Access Levels</option>
                        {uniqueAccessLevels.map(level => (
                            <option key={level} value={level} className="capitalize">{level}</option>
                        ))}
                    </select>

                    {/* Clear Filters Button */}
                    {activeFiltersCount > 0 && (
                        <button
                            onClick={clearFilters}
                            className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                        >
                            Clear Filters ({activeFiltersCount})
                        </button>
                    )}

                    {/* Results Count */}
                    <div className="ml-auto text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredEmployees.length} of {employees.length} employees
                    </div>
                </div>
            </div>

            {/* Table */}
            {filteredEmployees.length === 0 ? (
                <div className="p-8 text-center">
                    <Filter className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No employees match the selected filters</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">Try adjusting your filters to see more results.</p>
                    <button
                        onClick={clearFilters}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Clear All Filters
                    </button>
                </div>
            ) : (
                <div
                    className="relative overflow-x-auto flex-1 min-h-0 overflow-y-auto"
                    onScroll={(e) => {
                        const scrollLeft = e.target.scrollLeft;
                        // Near the start: show hint in idle state
                        if (scrollLeft <= 4) {
                            if (scrollHintBurstTimeoutRef.current) clearTimeout(scrollHintBurstTimeoutRef.current);
                            if (scrollHintIdleTimeoutRef.current) clearTimeout(scrollHintIdleTimeoutRef.current);
                            setIsScrolling(false);
                            setScrollHintHidden(false);
                            setScrollHintActive(false);
                            return;
                        }

                        // First movement after idle: burst once then hide
                        if (!isScrolling) {
                            setIsScrolling(true);
                            setScrollHintHidden(false);
                            setScrollHintActive(true);
                            if (scrollHintBurstTimeoutRef.current) clearTimeout(scrollHintBurstTimeoutRef.current);
                            scrollHintBurstTimeoutRef.current = setTimeout(() => {
                                setScrollHintActive(false);
                                setScrollHintHidden(true);
                            }, 320);
                        }

                        // Keep hidden while scrolling away from start
                        setScrollHintHidden(true);

                        // When scrolling stops for a bit and user is not near start, keep hidden
                        if (scrollHintIdleTimeoutRef.current) clearTimeout(scrollHintIdleTimeoutRef.current);
                        scrollHintIdleTimeoutRef.current = setTimeout(() => {
                            setIsScrolling(false);
                        }, 700);
                    }}
                >
                    <table className="min-w-full" style={{ minWidth: '1600px' }}>
                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Legal Entity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Department</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Designation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reporting Manager</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        {/*<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Onboarding</th>*/}
                        <th className="tour-primary-access-column px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Access Level</th>
                        <th className="tour-secondary-access-column px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Secondary Access</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                    </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                                </div>
                                <div>
                                    {editingEmployee === employee.id ? (
                                        <div className="space-y-1">
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={editFormData.first_name}
                                                    onChange={(e) => handleEditFieldChange('first_name', e.target.value)}
                                                    className="text-sm font-medium border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-20 dark:bg-gray-700 dark:text-white"
                                                    placeholder="First*"
                                                    required
                                                />
                                                <input
                                                    type="text"
                                                    value={editFormData.last_name}
                                                    onChange={(e) => handleEditFieldChange('last_name', e.target.value)}
                                                    className="text-sm font-medium border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-20 dark:bg-gray-700 dark:text-white"
                                                    placeholder="Last*"
                                                    required
                                                />
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{employee.employee_code}</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {employee.first_name} {employee.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{employee.employee_code}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {employee.legal_entity || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {editingEmployee === employee.id ? (
                                <select
                                    value={editFormData.department_id}
                                    onChange={(e) => handleEditFieldChange('department_id', e.target.value)}
                                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm w-full dark:bg-gray-700 dark:text-white"
                                >
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                </select>
                            ) : (
                                getDepartmentName(employee)
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {editingEmployee === employee.id ? (
                                <input
                                    type="text"
                                    value={editFormData.designation}
                                    onChange={(e) => handleEditFieldChange('designation', e.target.value)}
                                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm w-full dark:bg-gray-700 dark:text-white"
                                    placeholder="Designation*"
                                    required
                                />
                            ) : (
                                employee.designation
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {editingEmployee === employee.id ? (
                                <select
                                    value={editFormData.managerId}
                                    onChange={(e) => handleEditFieldChange('managerId', e.target.value)}
                                    className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm w-full dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">No Manager</option>
                                    {employees.filter(emp => emp.id !== employee.id).map(manager => (
                                        <option key={manager.id} value={manager.id}>
                                            {manager.name} {manager.designation ? `- ${manager.designation}` : ''}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                (() => {
                                    const manager = employees.find(emp => emp.id === (employee.managerId || employee.reporting_manager_id));
                                    return manager ? (
                                        <span className="text-gray-900 dark:text-gray-100">{manager.name || `${manager.first_name} ${manager.last_name}`}</span>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-400 dark:text-gray-500">No Manager</span>
                                            {employee.role !== 'admin' && (
                                                <button
                                                    onClick={() => handleAddManager(employee)}
                                                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                                    title="Add Reporting Manager"
                                                >
                                                    <UserPlus className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            {editingEmployee === employee.id ? (
                                <div className="space-y-2">
                                    <div className="flex items-center">
                                        <Mail className="w-3 h-3 mr-1 text-gray-400" />
                                        <input
                                            type="email"
                                            value={editFormData.email}
                                            onChange={(e) => handleEditFieldChange('email', e.target.value)}
                                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs w-full dark:bg-gray-700 dark:text-white"
                                            placeholder="Email*"
                                            required
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <Phone className="w-3 h-3 mr-1 text-gray-400 dark:text-gray-500" />
                                        <input
                                            type="tel"
                                            value={editFormData.phone}
                                            onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs w-full dark:bg-gray-700 dark:text-white"
                                            placeholder="Phone"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm">
                                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                                        <Mail className="w-3 h-3 mr-1" />
                                        {employee.email}
                                    </div>
                                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                                        <Phone className="w-3 h-3 mr-1" />
                                        {employee.phone || 'N/A'}
                                    </div>
                                </div>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                                const exitStatusBadge = getExitStatusBadge(employee, 'sm');
                                return (
                                    <span
                                        className={exitStatusBadge.className}
                                        title={exitStatusBadge.description}
                                    >
                                        {exitStatusBadge.label}
                                    </span>
                                );
                            })()}
                        </td>
                        {/*<td className="px-6 py-4 whitespace-nowrap">*/}
                        {/*    {employee.onboarding_status ? (*/}
                        {/*        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${*/}
                        {/*            employee.onboarding_status === 'completed'*/}
                        {/*                ? 'bg-green-100 text-green-800'*/}
                        {/*                : employee.onboarding_status === 'in-progress'*/}
                        {/*                    ? 'bg-yellow-100 text-yellow-800'*/}
                        {/*                    : 'bg-gray-100 text-gray-800'*/}
                        {/*        }`}>*/}
                        {/*            {employee.onboarding_status === 'completed' ? 'Yes' : 'No'}*/}
                        {/*        </span>*/}
                        {/*    ) : (*/}
                        {/*        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">*/}
                        {/*            No*/}
                        {/*        </span>*/}
                        {/*    )}*/}
                        {/*</td>*/}
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="relative">
                                <button
                                    onClick={() => setOpenAccessDropdown(openAccessDropdown === employee.id ? null : employee.id)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${getAccessLevelColor(employee.accessLevel || 'employee')}`}
                                >
                                    <Shield className="w-4 h-4" />
                                    <span className="text-xs font-medium">
                                        {allAccessTypes.find(a => a.id === (employee.accessLevel || 'employee'))?.name || 'Employee Access'}
                                    </span>
                                    <ChevronDown className="w-3 h-3" />
                                </button>

                                {openAccessDropdown === employee.id && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setOpenAccessDropdown(null)}
                                        />
                                        <div className="absolute left-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900 border border-gray-200 dark:border-gray-700 z-20 max-h-64 overflow-y-auto">
                                            <div className="py-1">
                                                {allAccessTypes.map((accessType) => (
                                                    <button
                                                        key={accessType.id}
                                                        onClick={() => handleAccessLevelChange(employee.id, accessType.id)}
                                                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                                            employee.accessLevel === accessType.id ? 'bg-blue-50 dark:bg-blue-900' : ''
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Shield className={`w-4 h-4 ${
                                                                employee.accessLevel === accessType.id ? 'text-blue-600' : 'text-gray-400'
                                                            }`} />
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900 dark:text-white">{accessType.name}</div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">{accessType.description}</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {employee.accessLevel === 'admin' || employee.access_level === 'admin' ? (
                                <select
                                    value={employee.secondaryAccess || employee.secondary_access || 'employee'}
                                    onChange={(e) => updateEmployee(employee.id, { secondaryAccess: e.target.value })}
                                    className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="manager">Manager Access</option>
                                    <option value="employee">Employee Access</option>
                                </select>
                            ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {editingEmployee === employee.id ? (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSaveEdit}
                                        className="text-green-600 hover:text-green-900"
                                        title="Save Changes"
                                    >
                                        <Save className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="text-red-600 hover:text-red-900"
                                        title="Cancel Edit"
                                    >
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => navigate(`/admin/employees/${employee.id}`)}
                                    className="tour-employee-view-details text-blue-600 hover:text-blue-900"
                                    title="View Details"
                                >
                                    <Eye className="w-4 h-4" />
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
                </tbody>
                </table>
                <div className="pointer-events-none absolute top-0 right-0 h-full w-28">
                    <div
                        className={`h-full w-full ${
                            scrollHintHidden ? 'scroll-hint-hidden' : scrollHintActive ? 'scroll-hint-burst' : 'scroll-hint-idle'
                        }`}
                        style={{
                            backgroundImage:
                                'radial-gradient(70% 120% at 100% 50%, rgba(255,140,0,0.55), rgba(255,140,0,0) 70%),' +
                                'linear-gradient(270deg, rgba(255,170,70,0.5), rgba(255,140,0,0)),' +
                                'linear-gradient(270deg, rgba(255,200,120,0.35), rgba(255,140,0,0) 60%)',
                            filter: 'blur(4px)',
                            transform: 'translateX(8px)'
                        }}
                    />
                </div>
            </div>
            )}

            {/* Add Manager Modal */}
            {showManagerModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={() => setShowManagerModal(false)}></div>

                        <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Add Reporting Manager</h2>
                                <button
                                    onClick={() => setShowManagerModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    Employee: <span className="font-medium text-gray-900 dark:text-white">{selectedEmployee?.name}</span>
                                </p>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Reporting Manager
                                </label>
                                <select
                                    value={selectedManagerId}
                                    onChange={(e) => setSelectedManagerId(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                    required
                                >
                                    <option value="">Choose a manager...</option>
                                    {potentialManagers.map(manager => (
                                        <option key={manager.id} value={manager.id}>
                                            {manager.name} - {manager.designation}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowManagerModal(false)}
                                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleManagerSubmit}
                                    disabled={!selectedManagerId}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Add Manager
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Portal Access Modal */}
            {showCreateAccessModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={() => setShowCreateAccessModal(false)}></div>

                        <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex items-center gap-2">
                                    <Shield className="w-6 h-6 text-purple-600" />
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Portal Access</h2>
                                </div>
                                <button
                                    onClick={() => setShowCreateAccessModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="mb-6">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Create custom portal access types for employees with specific roles, designations, or positions.
                                    These will be available in the Access Level dropdown for all employees.
                                </p>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Access Type Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={newAccessType.name}
                                            onChange={(e) => setNewAccessType({ ...newAccessType, name: e.target.value })}
                                            placeholder="e.g., Senior Manager, Team Lead, Analyst"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Description
                                        </label>
                                        <textarea
                                            value={newAccessType.description}
                                            onChange={(e) => setNewAccessType({ ...newAccessType, description: e.target.value })}
                                            placeholder="Describe the permissions and access level..."
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                                            rows="3"
                                        />
                                    </div>
                                </div>

                                {customAccessTypes.length > 0 && (
                                    <div className="mt-6">
                                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Custom Access Types ({customAccessTypes.length})</h3>
                                        <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {customAccessTypes.map((accessType, index) => (
                                                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                                    <Shield className="w-4 h-4 text-purple-600" />
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{accessType.name}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">{accessType.description}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateAccessModal(false)}
                                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleCreateCustomAccess}
                                    disabled={!newAccessType.name.trim()}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create Access Type
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
            <style>{`
          .scroll-hint-idle {
            opacity: 0.32;
            animation: airflowIdle 2.6s ease-in-out infinite;
            background-size: 200% 100%;
          }

          .scroll-hint-hidden {
            opacity: 0;
            transition: opacity 200ms ease-out;
          }

          .scroll-hint-burst {
            opacity: 0.85;
            animation: airflowBurst 0.55s ease-out forwards;
            background-size: 240% 100%;
          }

          @keyframes airflowIdle {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          @keyframes airflowBurst {
            0% { opacity: 0.9; transform: translateX(8px) scaleX(1); }
            60% { opacity: 0.55; transform: translateX(-4px) scaleX(1.12); }
            100% { opacity: 0; transform: translateX(-12px) scaleX(1.2); }
          }
        `}</style>
        </>
    );
};

export default EmployeeList;
